import { useState, useRef, useCallback, useEffect } from "react";
import { useCaptureCountdown } from "./useCaptureCountdown";
import { useGlareCheck } from "./useGlareCheck";
import { middlewareService } from "../services/middleware/middlewareService";

// EN-4743. One ID capture with an on-device read, from the camera going live to
// the photo. Shared by the passport (MRZ on the front) and the licence / state ID
// (PDF417 on the back); only the reader differs.
//
// Each scan attempt runs the kiosk countdown while the ID is read live:
//  - valid read  -> stop the countdown, take the photo and send it straight away
//                   with the extracted details (the kiosk waits for its countdown;
//                   visitors-web doesn't)
//  - no read, attempts left -> no photo; the visitor is offered Retry
//  - no read on the last attempt -> take the photo, give it one more read if the
//                   reader can read photos (licence barcode, like the kiosk's
//                   full-resolution photo pass), and send it automatically, with
//                   the details if that read worked, photo only if not
// The retry prompt and the automatic send are visitors-web's own; the kiosk has
// neither.
//
// The 3 attempts are one budget per ID capture, shared with the backend's answer:
// a scan miss uses one (Retry above), and so does a non-valid validateId result,
// which the page reports with nextAttemptAfterRejection() when it offers Retry
// from its Invalid modal. Retake / Start Camera / Back only restart the current
// attempt (restartAttempt), so they never hand out extra attempts.
export const MAX_SCAN_ATTEMPTS = 3;

export type IdScanState =
  // Not scanning: before the camera starts, or not this ID type.
  | "idle"
  // Countdown running, reading live.
  | "scanning"
  // Countdown ended with no read and attempts left.
  | "awaitingRetry"
  // Photo taken (with or without a read). Nothing more happens in this session.
  | "photoTaken"
  // The reader can't run on this device: today's manual flow.
  | "unavailable";

export interface IdScannerHookOptions<T> {
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  active: boolean;
  onConfirmed?: (result: T) => void;
}

export interface IdScanner {
  reset: () => void;
  getStats: () => { passes: number };
}

// Telemetry event names. Only counts and timings are logged, never what was read.
export interface IdScanEvents {
  read: string;
  attemptFailed: string;
  fallbackSubmit: string;
  unavailable: string;
  // validateId said the ID isn't valid and the visitor was offered Retry.
  rejected: string;
  // One row per capture that ended with a photo from the scan: how it went.
  summary?: string;
}

interface UseIdScanSessionOptions<T, S> {
  enabled: boolean;
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  // Takes the photo, the same way the Capture button does. Returns it (a data
  // URL) when the session should read it again after the last miss.
  takePhoto: () => string | null | void;
  // Sends the photo for validation without the visitor tapping Continue.
  autoSubmit: () => void;
  // The live reader. Must be the same hook on every render.
  useScanner: (options: IdScannerHookOptions<T>) => IdScanner & { result: T | null };
  isReaderReady: () => Promise<boolean>;
  readerUnavailableReason: () => string | null;
  // Optional second chance after the last miss. grabStill runs first, while the
  // camera is still on screen (a full-resolution still of the card); readPhoto
  // then reads that still, or the photo if there is none.
  grabStill?: () => Promise<S | null>;
  readPhoto?: (photo: string | null, still: S | null) => Promise<T | null>;
  // Frees a still that was grabbed but won't be read (the visitor left meanwhile).
  releaseStill?: (still: S) => void;
  // Extra, non-identifying fields for the summary row (camera, decoder...).
  getSummary?: () => Record<string, unknown>;
  events: IdScanEvents;
}

export interface UseIdScanSessionReturn<T> {
  state: IdScanState;
  // The attempt in progress, 1 to MAX_SCAN_ATTEMPTS.
  attempt: number;
  // Attempts left after this one.
  attemptsLeft: number;
  secondsLeft: number;
  countdownRunning: boolean;
  isGlare: boolean;
  // The read, live or from the photo. Null when nothing was read.
  data: T | null;
  // The photo is being read after the last miss; it's sent when this finishes.
  readingPhoto: boolean;
  // Call when the camera starts streaming.
  onCameraLive: () => void;
  // Start the next attempt from the retry prompt.
  retry: () => void;
  // Start the current attempt again, keeping its number: Retake, Start Camera,
  // leaving the back step.
  restartAttempt: () => void;
  // validateId said the ID isn't valid and the visitor chose Retry: move on to the
  // next attempt (never past the last) and wait for the camera again.
  nextAttemptAfterRejection: () => void;
  // Back to attempt 1 with nothing kept.
  reset: () => void;
  // The visitor took the photo with the Capture button.
  onManualCapture: () => void;
}

export const useIdScanSession = <T, S = never>({
  enabled,
  videoEl,
  stageEl,
  guideEl,
  takePhoto,
  autoSubmit,
  useScanner,
  isReaderReady,
  readerUnavailableReason,
  grabStill,
  readPhoto,
  releaseStill,
  getSummary,
  events,
}: UseIdScanSessionOptions<T, S>): UseIdScanSessionReturn<T> => {
  const [state, setState] = useState<IdScanState>("idle");
  const [attempt, setAttempt] = useState<number>(1);
  const [readerUnavailable, setReaderUnavailable] = useState<boolean>(false);
  // Same value, for callbacks that must see it at once.
  const readerUnavailableRef = useRef<boolean>(false);
  const [photoData, setPhotoData] = useState<T | null>(null);
  const [readingPhoto, setReadingPhoto] = useState<boolean>(false);
  const stateRef = useRef<IdScanState>("idle");
  const attemptRef = useRef<number>(1);
  const readStartedAtRef = useRef<number>(0);
  // Bumped by reset(), so a photo read still running for an old capture is dropped.
  const sessionRef = useRef<number>(0);

  const moveTo = useCallback((next: IdScanState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  // Callbacks from the page can change every render; always call the latest.
  const takePhotoRef = useRef(takePhoto);
  const autoSubmitRef = useRef(autoSubmit);
  const readPhotoRef = useRef(readPhoto);
  const grabStillRef = useRef(grabStill);
  const releaseStillRef = useRef(releaseStill);
  const getSummaryRef = useRef(getSummary);
  const eventsRef = useRef(events);
  useEffect(() => {
    takePhotoRef.current = takePhoto;
    autoSubmitRef.current = autoSubmit;
    readPhotoRef.current = readPhoto;
    grabStillRef.current = grabStill;
    releaseStillRef.current = releaseStill;
    getSummaryRef.current = getSummary;
    eventsRef.current = events;
  }, [takePhoto, autoSubmit, readPhoto, grabStill, releaseStill, getSummary, events]);

  // Filled in once the countdown exists (it's created after the scanner below).
  const stopCountdownRef = useRef<() => void>(() => undefined);
  const getStatsRef = useRef<IdScanner["getStats"]>(() => ({ passes: 0 }));

  // One summary row per capture that the scan finished (counts and settings only).
  const logSummary = useCallback((outcome: "read" | "none", source: string | null, msToRead: number, extra: Record<string, number> = {}) => {
    const name = eventsRef.current.summary;
    if (!name) return;
    middlewareService.logInfo(name, {
      outcome,
      source,
      attempt: attemptRef.current,
      msToRead,
      ...extra,
      ...getStatsRef.current(),
      ...(getSummaryRef.current?.() ?? {}),
    });
  }, []);

  // Rev 4: a confirmed read takes the photo and sends it straight away, with the
  // extracted details. Unlike the kiosk, which always waits for its countdown.
  const onReadConfirmed = useCallback(() => {
    if (stateRef.current !== "scanning") return;
    const msToRead = Math.round(performance.now() - readStartedAtRef.current);
    middlewareService.logInfo(eventsRef.current.read, {
      attempt: attemptRef.current,
      passes: getStatsRef.current().passes,
      msToRead,
      source: "live",
    });
    logSummary("read", "live", msToRead);
    stopCountdownRef.current();
    moveTo("photoTaken");
    takePhotoRef.current();
    autoSubmitRef.current();
  }, [moveTo, logSummary]);

  const glare = useGlareCheck({ videoEl, stageEl, guideEl, active: enabled && state === "scanning" });
  const {
    result: liveData,
    reset: resetScanner,
    getStats,
  } = useScanner({ videoEl, stageEl, guideEl, active: enabled && state === "scanning", onConfirmed: onReadConfirmed });
  const resultRef = useRef<T | null>(null);
  useEffect(() => {
    resultRef.current = liveData;
    getStatsRef.current = getStats;
  }, [liveData, getStats]);

  // Last attempt missed: take the photo, read it once more if the reader can,
  // then send, with the details or photo only.
  const takeFallbackPhoto = useCallback(() => {
    const stats = getStats();
    moveTo("photoTaken");
    const read = readPhotoRef.current;
    const startedAt = performance.now();
    if (!read) {
      takePhotoRef.current();
      middlewareService.logWarn(eventsRef.current.fallbackSubmit, { attempts: MAX_SCAN_ATTEMPTS, passes: stats.passes });
      logSummary("none", null, Math.round(performance.now() - readStartedAtRef.current));
      autoSubmitRef.current();
      return;
    }
    const session = sessionRef.current;
    setReadingPhoto(true);
    void (async () => {
      // While the camera is still on screen: the best still of the card.
      const grab = grabStillRef.current;
      const still = grab ? await grab().catch(() => null) : null;
      const stillMs = Math.round(performance.now() - startedAt);
      if (session !== sessionRef.current) {
        if (still) releaseStillRef.current?.(still);
        return;
      }
      const photo = takePhotoRef.current();
      const readStartedAt = performance.now();
      const hit = await read(typeof photo === "string" && photo ? photo : null, still).catch(() => null);
      if (session !== sessionRef.current) return;
      const msToRead = Math.round(performance.now() - startedAt);
      const photoReadMs = Math.round(performance.now() - readStartedAt);
      if (hit) {
        middlewareService.logInfo(eventsRef.current.read, { attempt: MAX_SCAN_ATTEMPTS, passes: stats.passes, msToRead, source: "photo" });
      } else {
        middlewareService.logWarn(eventsRef.current.fallbackSubmit, { attempts: MAX_SCAN_ATTEMPTS, passes: stats.passes, msToRead });
      }
      logSummary(hit ? "read" : "none", "photo", msToRead, { stillMs, photoReadMs });
      setPhotoData(hit);
      setReadingPhoto(false);
      autoSubmitRef.current();
    })();
  }, [getStats, moveTo, logSummary]);

  const onCountdownEnd = useCallback(() => {
    if (stateRef.current !== "scanning") return;
    // Safety net: a read confirmed in the same tick the countdown ended.
    if (resultRef.current) {
      moveTo("photoTaken");
      takePhotoRef.current();
      autoSubmitRef.current();
      return;
    }
    if (attemptRef.current < MAX_SCAN_ATTEMPTS) {
      middlewareService.logWarn(eventsRef.current.attemptFailed, { attempt: attemptRef.current, ...getStats() });
      moveTo("awaitingRetry");
      return;
    }
    takeFallbackPhoto();
  }, [getStats, moveTo, takeFallbackPhoto]);

  const {
    secondsLeft,
    running: countdownRunning,
    start: startCountdown,
    stop: stopCountdown,
  } = useCaptureCountdown({ onElapsed: onCountdownEnd, isGlare: glare.isGlareNow });
  useEffect(() => {
    stopCountdownRef.current = stopCountdown;
  }, [stopCountdown]);

  // Reader not usable on this device: stop scanning and give the visitor today's flow.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    isReaderReady().then((ready) => {
      if (cancelled || ready) return;
      readerUnavailableRef.current = true;
      setReaderUnavailable(true);
      middlewareService.logWarn(eventsRef.current.unavailable, { reason: readerUnavailableReason() });
      // Mid-scan, or on the Retry screen: switch to the manual flow (Capture) at once.
      // On the Retry screen only Retry is shown, so staying there would offer a scan
      // that can't read anything.
      if (stateRef.current === "scanning" || stateRef.current === "awaitingRetry") {
        stopCountdown();
        moveTo("unavailable");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, isReaderReady, readerUnavailableReason, stopCountdown, moveTo]);

  const startAttempt = useCallback(() => {
    readStartedAtRef.current = performance.now();
    moveTo("scanning");
    startCountdown();
  }, [startCountdown, moveTo]);

  const onCameraLive = useCallback(() => {
    if (!enabled || stateRef.current !== "idle") return;
    if (readerUnavailable) {
      moveTo("unavailable");
      return;
    }
    startAttempt();
  }, [enabled, readerUnavailable, moveTo, startAttempt]);

  const retry = useCallback(() => {
    if (stateRef.current !== "awaitingRetry") return;
    // The reader failed meanwhile: no more scans, go to the manual flow.
    if (readerUnavailableRef.current) {
      moveTo("unavailable");
      return;
    }
    attemptRef.current += 1;
    setAttempt(attemptRef.current);
    resetScanner();
    startAttempt();
  }, [resetScanner, startAttempt, moveTo]);

  // Stop the countdown, drop the live read and any photo read still running, and
  // wait for the camera again. The attempt number is kept.
  const restartAttempt = useCallback(() => {
    sessionRef.current += 1;
    stopCountdown();
    resetScanner();
    setPhotoData(null);
    setReadingPhoto(false);
    moveTo("idle");
  }, [stopCountdown, resetScanner, moveTo]);

  const nextAttemptAfterRejection = useCallback(() => {
    middlewareService.logInfo(eventsRef.current.rejected, { attempt: attemptRef.current });
    attemptRef.current = Math.min(attemptRef.current + 1, MAX_SCAN_ATTEMPTS);
    setAttempt(attemptRef.current);
    restartAttempt();
  }, [restartAttempt]);

  const reset = useCallback(() => {
    attemptRef.current = 1;
    setAttempt(1);
    restartAttempt();
  }, [restartAttempt]);

  const onManualCapture = useCallback(() => {
    stopCountdown();
    if (stateRef.current !== "idle") moveTo("photoTaken");
  }, [stopCountdown, moveTo]);

  return {
    state,
    attempt,
    attemptsLeft: MAX_SCAN_ATTEMPTS - attempt,
    secondsLeft,
    countdownRunning,
    isGlare: glare.isGlare,
    data: liveData ?? photoData,
    readingPhoto,
    onCameraLive,
    retry,
    restartAttempt,
    nextAttemptAfterRejection,
    reset,
    onManualCapture,
  };
};
